import { apiRequest } from '../api/client';
import type { InvoiceType } from '../invoices/api';

export const ACCOUNT_MOVEMENT_KINDS = ['INITIAL_BALANCE', 'INVOICE', 'PAYMENT'] as const;
export type AccountMovementKind = (typeof ACCOUNT_MOVEMENT_KINDS)[number];

/** Matches `apps/api` `AccountStatementMovementDto` (`get-account-statement.use-case.ts`). */
export interface AccountMovement {
  kind: AccountMovementKind;
  date: string;
  amountCents: number;
  balanceAfterCents: number;
  invoiceId: string | null;
  invoiceNumber: string | null;
  /** Optional on older API builds; without them the page falls back to the bare number. */
  invoicePointOfSale?: number | null;
  invoiceType?: InvoiceType | null;
  paymentId: string | null;
  receiptNumber: string | null;
}

/** Matches `apps/api` `AccountStatementSummaryDto`. */
export interface AccountStatementSummary {
  totalInvoicedCents: number;
  totalCollectedCents: number;
  pendingBalanceCents: number;
  overdueAmountCents: number;
  unallocatedCreditCents: number;
}

/** Matches `apps/api` `AccountStatementResult`, the shape `GET /customers/:customerId/account` returns. */
export interface AccountStatement {
  movements: AccountMovement[];
  summary: AccountStatementSummary;
}

export interface AccountStatementParams {
  fromDate?: string | undefined;
  toDate?: string | undefined;
}

/** Matches `apps/api` `GET /customers/:customerId/account`. */
export function getAccountStatement(
  customerId: string,
  params: AccountStatementParams = {},
): Promise<AccountStatement> {
  const query = new URLSearchParams();

  if (params.fromDate !== undefined) query.set('fromDate', params.fromDate);
  if (params.toDate !== undefined) query.set('toDate', params.toDate);

  const queryString = query.toString();

  return apiRequest<AccountStatement>(
    `/customers/${customerId}/account${queryString === '' ? '' : `?${queryString}`}`,
  );
}

export const AGING_BUCKET_LABELS_ORDER = ['CURRENT', '0-30', '31-60', '61-90', '90+'] as const;
export type AgingBucketLabel = (typeof AGING_BUCKET_LABELS_ORDER)[number];

/** Matches `apps/api` `AgingBucketInvoiceDto`. */
export interface AgingBucketInvoice {
  invoiceId: string;
  invoiceNumber: string;
  dueDate: string | null;
  balanceCents: number;
}

/** Matches `apps/api` `AgingBucketDto`, the shape `GET /customers/:customerId/aging` returns (one entry per bucket). */
export interface AgingBucket {
  bucket: AgingBucketLabel;
  invoiceCount: number;
  totalCents: number;
  invoices: AgingBucketInvoice[];
}

/** Matches `apps/api` `GET /customers/:customerId/aging`. */
export function getAgingReport(customerId: string): Promise<AgingBucket[]> {
  return apiRequest<AgingBucket[]>(`/customers/${customerId}/aging`);
}
